"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuthFlow } from "../../auth-flow-context";
import { AuthMethodId } from "../../types";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { PhoneAuthStep, usePhoneAuth } from "./phone-auth-context";
import { PhoneAuthError } from "./phone-auth-error";
import { usePhoneCodeCooldown } from "./use-phone-code-cooldown";
type CodeEntryProps = {
  className?: string;
};
export function CodeEntry(t0) {
  const $ = _c(54);
  const {
    className
  } = t0;
  const {
    state,
    actions
  } = usePhoneAuth();
  const phoneError = useAuthFlow().state.errors[AuthMethodId.PHONE];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Array(6).fill("");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [digits, setDigits] = useState(t1);
  const {
    isCoolingDown,
    secondsRemaining,
    startCooldown
  } = usePhoneCodeCooldown(true);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const inputRefs = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => inputRefs.current[index]?.focus();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const focusBox = t3;
  const shouldReset = !!phoneError || state.step !== PhoneAuthStep.ENTER_CODE;
  let t4;
  let t5;
  if ($[3] !== shouldReset) {
    t4 = () => {
      if (!shouldReset) {
        return;
      }
      setDigits(Array(6).fill(""));
      focusBox(0);
    };
    t5 = [shouldReset];
    $[3] = shouldReset;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  if (state.step !== PhoneAuthStep.ENTER_CODE) {
    return null;
  }
  if (state.isVerifyingCode) {
    let t6;
    if ($[6] !== className) {
      t6 = cx(className);
      $[6] = className;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="mx-auto h-48 w-full" />;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== t6) {
      t8 = <div className={t6} role="status" aria-live="polite" aria-label={"Verifying code\u2026"}>{t7}</div>;
      $[9] = t6;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    return t8;
  }
  let t6;
  if ($[11] !== actions || $[12] !== state.isVerifyingCode) {
    t6 = async code => {
      if (code.length === 6 && !state.isVerifyingCode) {
        await actions.submitCode(code);
      }
    };
    $[11] = actions;
    $[12] = state.isVerifyingCode;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const submitFull = t6;
  let t7;
  if ($[14] !== digits || $[15] !== submitFull) {
    t7 = (value, index_0) => {
      if (!/^[0-9]?$/.test(value)) {
        return;
      }
      const updated = [...digits];
      updated[index_0] = value;
      setDigits(updated);
      if (value && index_0 < 5) {
        focusBox(index_0 + 1);
      }
      const codeString = updated.join("");
      if (value && index_0 === 5 && updated.every(_temp)) {
        submitFull(codeString);
      }
    };
    $[14] = digits;
    $[15] = submitFull;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleChange = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (e, index_1) => {
      if (e.key === "Backspace" && index_1 > 0) {
        setDigits(prev => {
          const next = [...prev];
          next[index_1] = "";
          return next;
        });
        focusBox(index_1 - 1);
      }
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleKeyDown = t8;
  let t9;
  if ($[18] !== submitFull) {
    t9 = e_0 => {
      e_0.preventDefault();
      const pasted = e_0.clipboardData.getData("text").trim();
      if (/^\d{6}$/.test(pasted)) {
        setDigits(pasted.split(""));
        submitFull(pasted);
      }
    };
    $[18] = submitFull;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handlePaste = t9;
  let t10;
  if ($[20] !== submitFull) {
    t10 = e_1 => {
      const value_0 = e_1.currentTarget.value;
      if (value_0.length === 6) {
        setDigits(value_0.split(""));
        submitFull(value_0);
      }
    };
    $[20] = submitFull;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleAutoFill = t10;
  let t11;
  if ($[22] !== actions || $[23] !== isCoolingDown || $[24] !== startCooldown || $[25] !== state.isSendingCode) {
    t11 = async () => {
      if (isCoolingDown || state.isSendingCode) {
        return;
      }
      startCooldown();
      setDigits(Array(6).fill(""));
      await actions.resend();
    };
    $[22] = actions;
    $[23] = isCoolingDown;
    $[24] = startCooldown;
    $[25] = state.isSendingCode;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleResend = t11;
  let t12;
  if ($[27] !== className) {
    t12 = cx(className);
    $[27] = className;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <h2 className="text-h2">Enter Verification Code</h2>;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== state.phoneNumber) {
    t14 = formatPhoneNumber(state.phoneNumber, false);
    $[30] = state.phoneNumber;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <div className="space-y-2 text-center">{t13}<p className="text-body text-foreground-secondary">We've sent a 6-digit code to{" "}<span className="font-medium text-primary">{t14}</span></p></div>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== digits || $[35] !== handleAutoFill || $[36] !== handleChange || $[37] !== handlePaste || $[38] !== state.isVerifyingCode) {
    t16 = digits.map((digit, index_2) => <input key={index_2} ref={el => {
      inputRefs.current[index_2] = el;
    }} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete={index_2 === 0 ? "one-time-code" : "off"} value={digit} maxLength={index_2 === 0 ? 6 : 1} autoFocus={index_2 === 0} onChange={e_2 => handleChange(e_2.target.value, index_2)} onInput={index_2 === 0 ? handleAutoFill : undefined} onKeyDown={e_3 => handleKeyDown(e_3, index_2)} onPaste={handlePaste} disabled={state.isVerifyingCode} className={cx("h-12 w-12 rounded-md border bg-background text-center text-h3 text-foreground focus:outline-none focus:ring-1", "border-border focus:border-primary focus:ring-primary")} />);
    $[34] = digits;
    $[35] = handleAutoFill;
    $[36] = handleChange;
    $[37] = handlePaste;
    $[38] = state.isVerifyingCode;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16) {
    t17 = <div className="grid grid-cols-6 gap-2">{t16}</div>;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="mb-2 text-caption text-foreground-secondary">Didn't receive the code?</p>;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = isCoolingDown || state.isSendingCode;
  const t20 = isCoolingDown ? `Resend in ${secondsRemaining}s` : "Resend Code";
  let t21;
  if ($[43] !== handleResend || $[44] !== state.isSendingCode || $[45] !== t19 || $[46] !== t20) {
    t21 = <div className="text-center">{t18}<Button variant="ghost" onClick={handleResend} disabled={t19} isLoading={state.isSendingCode} loadingText="Sending...">{t20}</Button></div>;
    $[43] = handleResend;
    $[44] = state.isSendingCode;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <PhoneAuthError />;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t12 || $[50] !== t15 || $[51] !== t17 || $[52] !== t21) {
    t23 = <div className={t12}>{t15}{t17}{t21}{t22}</div>;
    $[49] = t12;
    $[50] = t15;
    $[51] = t17;
    $[52] = t21;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
}
function _temp(d) {
  return d !== "";
}